import { useId, useState, type FormEvent, type ReactNode } from "react";

import { inputClass } from "components/FormV2/fields/chrome";
import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";

interface CorrectionRowProps {
  /** The value being confirmed, as a sentence: "Confirmation goes to <b>…</b>". */
  statement: ReactNode;
  /** The prompt that opens the editor: "Not right? Fix it". */
  prompt: string;
  fieldLabel: string;
  inputType?: "email" | "tel" | "text";
  autoComplete?: string;
  /** Controlled so the host can open the editor itself, such as when the
   * server refuses the value. */
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /** What the field holds when the editor opens. */
  initialValue: string;
  /** Null when the draft is acceptable, otherwise the message to show. */
  validate: (draft: string) => string | null;
  /** Called with the trimmed draft once it validates. */
  onApply: (value: string) => void;
  /** "alert" paints the statement as a failure; the prompt and editor keep
   * their own styling. */
  tone?: "neutral" | "alert";
  /** The initial value was refused by the server: the field reads as
   * invalid until the draft changes. */
  initialValueRefused?: boolean;
}

interface EditorProps {
  fieldLabel: string;
  inputType: "email" | "tel" | "text";
  autoComplete?: string;
  initialValue: string;
  validate: (draft: string) => string | null;
  onApply: (value: string) => void;
  onCancel: () => void;
  initialValueRefused: boolean;
}

const MESSAGE_CLASS = "mt-2 text-sm text-red-800";

function Editor({
  fieldLabel,
  inputType,
  autoComplete,
  initialValue,
  validate,
  onApply,
  onCancel,
  initialValueRefused,
}: EditorProps) {
  const [draft, setDraft] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [edited, setEdited] = useState(false);
  const invalid = error !== null || (initialValueRefused && !edited);
  const fieldId = useId();
  const messageId = `${fieldId}-message`;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = validate(draft);
    setError(problem);
    if (problem === null) onApply(draft.trim());
  };

  return (
    <form onSubmit={submit} noValidate className="mt-2">
      <label className="sr-only" htmlFor={fieldId}>
        {fieldLabel}
      </label>
      <div className="flex items-center gap-3">
        <input
          id={fieldId}
          type={inputType}
          autoComplete={autoComplete}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
            setEdited(true);
          }}
          aria-invalid={invalid}
          aria-describedby={error === null ? undefined : messageId}
          className={`ph-no-capture min-w-0 flex-1 ${inputClass(invalid)}`}
        />
        <button
          type="submit"
          className={`${CONTINUE_BUTTON_CLASS} h-12 shrink-0`}
        >
          Update
        </button>
      </div>
      <button
        type="button"
        className={`${LINK_BUTTON_CLASS} mt-3 text-[15px]`}
        onClick={onCancel}
      >
        Cancel
      </button>
      {error !== null && (
        <p id={messageId} role="alert" className={MESSAGE_CLASS}>
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * A value with a way to correct it in place: the statement, then
 * either the prompt or — once it is tapped — the editor in the prompt's
 * place. Content below moves when it opens.
 */
export default function CorrectionRow({
  statement,
  prompt,
  fieldLabel,
  inputType = "text",
  autoComplete,
  editing,
  onEdit,
  onCancel,
  initialValue,
  validate,
  onApply,
  tone = "neutral",
  initialValueRefused = false,
}: CorrectionRowProps) {
  return (
    <div className="text-base leading-normal text-soil">
      <p className={tone === "alert" ? "text-red-800" : undefined}>
        {statement}
      </p>
      {editing ? (
        <Editor
          fieldLabel={fieldLabel}
          inputType={inputType}
          autoComplete={autoComplete}
          initialValue={initialValue}
          validate={validate}
          onApply={onApply}
          onCancel={onCancel}
          initialValueRefused={initialValueRefused}
        />
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className={`${LINK_BUTTON_CLASS} mt-1 text-[15px]`}
        >
          {prompt}
        </button>
      )}
    </div>
  );
}
